import { useState, useContext, FC } from 'react';
import { useModal } from '@blueprint/ui';
import {
    FiltersModal,
    DesktopFiltersButtonWrapper
} from '@zoopla/listing-search/filters/modal';
import { MobileQuickSearchButton } from '@zoopla/listing-search/button';
import { SearchInitiation } from '@zoopla/listing-search/search-initiation';
import { ModalManager } from '@zoopla/listing-search/modal-manager';
import { SearchContext } from '@zoopla/listing-search-context';

import {
    DesktopSubHeaderWrapper,
    StyledMobileQuickSearchBar,
    LocationWrapper,
    StickyQuickSearchBarWrapper,
    StickyQuickSearchBarDynamicShadow,
    StickyDesktopSubHeaderWrapper,
    StickyDesktopSubHeaderDynamicShadow,
    SearchInputWrapper
} from './Layout.styles';
import { Box } from '@zoopla-eng/zds-components/box';

type ModalTypes = 'search_initiation' | null;
enum LayoutVersion {
    Mobile = 'mobile',
    Desktop = 'desktop'
}

const mobileModalUpdateResultsText = 'Update results';

type SearchAndFiltersWrapperProps = {
    showFiltersOnDesktop?: boolean;
};

const SearchAndFiltersWrapper: FC<
    React.PropsWithChildren<SearchAndFiltersWrapperProps>
> = ({ children, showFiltersOnDesktop = false }) => {
    const { state } = useContext(SearchContext);

    const [modalType, setModalType] = useState<ModalTypes | null>(null);
    const [isModalShowing, openModal, closeModal] = useModal();

    const openModalType = (type: ModalTypes) => {
        setModalType(type);
        openModal(null);
    };

    const closeModalType = () => {
        setModalType(null);
        closeModal();
    };

    const { searchType } = state;

    return (
        <Box fw>
            <StickyQuickSearchBarDynamicShadow data-testid="search-and-filters">
                <StickyQuickSearchBarWrapper>
                    <StyledMobileQuickSearchBar>
                        <LocationWrapper>
                            <MobileQuickSearchButton
                                openModal={openModalType}
                            />
                        </LocationWrapper>
                        <FiltersModal
                            version={LayoutVersion.Mobile}
                            testId="mobile-sub-header_filters-button"
                        />
                    </StyledMobileQuickSearchBar>
                </StickyQuickSearchBarWrapper>
            </StickyQuickSearchBarDynamicShadow>
            {showFiltersOnDesktop && (
                <DesktopSubHeaderWrapper
                    data-testid="search-desktop-subheader"
                    isStickyDesktopBar={false}
                >
                    <SearchInputWrapper>
                        <SearchInitiation
                            searchType={searchType}
                            eventCategory="search results"
                            searchSource="refine"
                        />
                    </SearchInputWrapper>

                    <DesktopFiltersButtonWrapper>
                        <FiltersModal
                            version={LayoutVersion.Desktop}
                            testId="search-results-header_filters-button"
                        />
                    </DesktopFiltersButtonWrapper>
                </DesktopSubHeaderWrapper>
            )}
            {!showFiltersOnDesktop && (
                <StickyDesktopSubHeaderDynamicShadow data-testid="search-and-filters">
                    <StickyDesktopSubHeaderWrapper>
                        <DesktopSubHeaderWrapper
                            data-testid="search-desktop-subheader"
                            isStickyDesktopBar
                        >
                            <SearchInputWrapper>
                                <SearchInitiation
                                    searchType={searchType}
                                    eventCategory="search results"
                                    searchSource="refine"
                                />
                            </SearchInputWrapper>
                            <DesktopFiltersButtonWrapper>
                                <FiltersModal
                                    version={LayoutVersion.Desktop}
                                    testId="search-results-header_filters-button"
                                />
                            </DesktopFiltersButtonWrapper>
                        </DesktopSubHeaderWrapper>
                    </StickyDesktopSubHeaderWrapper>
                </StickyDesktopSubHeaderDynamicShadow>
            )}
            {children}
            <ModalManager
                modalType={modalType}
                modalProps={{
                    updateResultsText: mobileModalUpdateResultsText,
                    enableSearchTypeTabs: false,
                    searchType,
                    eventCategory: 'search results',
                    searchSource: 'facets'
                }}
                isModalShowing={isModalShowing}
                closeModal={closeModalType}
            />
        </Box>
    );
};

export default SearchAndFiltersWrapper;
